/**
 * HelpPage.js - How-to guide for LeagueMaker
 * Accessible at /help (public) and from NavBar (authenticated)
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import Logo from './Logo';
import './HelpPage.css';

const SECTIONS = [
  {
    title: 'Creating a League',
    content: (
      <>
        <p>LeagueMaker walks you through a 5-step process to create your league:</p>
        <ol>
          <li><strong>League Info</strong> — Name your league, pick a sport, set the season year, and enter your city and state.</li>
          <li><strong>Game Dates</strong> — Select the dates your league will play. Use the calendar picker to add or remove dates.</li>
          <li><strong>Time Slots</strong> — Define what times games start each day. You can optionally assign a venue to each time slot.</li>
          <li><strong>Teams</strong> — Add your team names and assign captains from your captain library (or create new ones inline).</li>
          <li><strong>Review &amp; Create</strong> — Confirm everything looks right, then create your league. The schedule is generated automatically.</li>
        </ol>
        <p>Your league starts in <strong>Draft</strong> mode so you can review the schedule and fairness report before publishing.</p>
      </>
    ),
  },
  {
    title: 'Managing Teams',
    content: (
      <>
        <p>Teams are set up in Step 4 when creating a league. You can configure:</p>
        <ul>
          <li><strong>Team names</strong> — Each team needs a unique name within the league.</li>
          <li><strong>Groups</strong> — Split teams into two round-robin groups if needed (toggle "Add a second group" in Step 1). Each group plays its own round-robin schedule.</li>
          <li><strong>Captains</strong> — Assign captains from your captain library. Captains are reusable across all your leagues.</li>
        </ul>
        <p>To manage your captain library, go to <strong>Captains</strong> in the navigation bar. You can add, edit, and delete captains there. When you assign a captain to a team, their contact info travels with them.</p>
      </>
    ),
  },
  {
    title: 'Understanding the Schedule & Fairness',
    content: (
      <>
        <p>When you create a league, the scheduling algorithm generates a balanced schedule automatically. It optimizes for three things:</p>
        <ol>
          <li><strong>Early/late balance</strong> — Every team gets an equal number of games at the earliest and latest time slots.</li>
          <li><strong>Overall time slot distribution</strong> — All time slots are spread evenly across teams.</li>
          <li><strong>Matchup balance</strong> — Every team plays every other team a similar number of times.</li>
        </ol>
        <p>While in Draft mode, a <strong>Fairness Report</strong> appears on each league card showing:</p>
        <ul>
          <li>A letter grade (<strong>A</strong>, <strong>B</strong>, or <strong>C</strong>)</li>
          <li>A summary of how balanced the schedule is</li>
          <li>Time slot distribution and matchup tables</li>
        </ul>
        <p>The fairness report is also included in the draft PDF export.</p>
      </>
    ),
  },
  {
    title: 'Publishing Your League',
    content: (
      <>
        <p>Once you're happy with the schedule, publish it to make it live:</p>
        <ol>
          <li>On the Dashboard, find your league card and toggle <strong>Public Page</strong> on.</li>
          <li>Your league gets a shareable URL at <code>/public/your-league-slug</code>.</li>
          <li>The public page shows the schedule, standings (if enabled), and calendar feeds.</li>
        </ol>
        <p>You can customize the public page (Starter plan and above) with:</p>
        <ul>
          <li>A custom URL slug</li>
          <li>A league logo</li>
          <li>A short description</li>
          <li>A custom brand color</li>
        </ul>
        <p>Click <strong>Customize</strong> next to the Public Page toggle to set these up.</p>
      </>
    ),
  },
  {
    title: 'Entering Scores & Viewing Standings',
    content: (
      <>
        <p>Score entry is available on the Commissioner (Pro) plan. There are several ways to enter scores:</p>
        <ul>
          <li><strong>Game Management page</strong> — Click "Manage Games" on a league card, then enter scores inline for each game.</li>
          <li><strong>Bulk score entry</strong> — Toggle "Bulk Score" in the Game Management action bar to score an entire week at once.</li>
          <li><strong>Captain submissions</strong> — Enable "Captain Score Submission" and captains can submit scores through their portal for your approval.</li>
          <li><strong>Referee submissions</strong> — Enable "Referee Score Submission" and assigned referees can submit scores for approval.</li>
        </ul>
        <p>Standings update automatically as scores are entered. They're visible on the public page (when "Show Standings" is enabled) and in the captain portal.</p>
        <p>The standings sort order and stat columns depend on your league's sport. For example, soccer uses goals for/against while basketball uses points for/against.</p>
      </>
    ),
  },
  {
    title: 'Calendar Subscriptions',
    content: (
      <>
        <p>Every public league page has a <strong>Calendar</strong> tab where captains and players can subscribe to game schedules:</p>
        <ul>
          <li><strong>Full league calendar</strong> — Subscribe to see all games across all teams.</li>
          <li><strong>Per-team calendar</strong> — Subscribe to only one team's games.</li>
        </ul>
        <p>Calendar feeds use the iCal format and work with Apple Calendar, Google Calendar, Outlook, and most other calendar apps. Events update automatically as scores are entered or games are rescheduled.</p>
        <p>On desktop, clicking "Subscribe" opens your default calendar app. On mobile, it may download the file for you to open.</p>
      </>
    ),
  },
  {
    title: 'Managing Venues',
    content: (
      <>
        <p>Venues are available on the League (Starter) plan and above. They're managed from the <strong>Venues</strong> page in the navigation bar.</p>
        <ul>
          <li>Venues belong to your account, not a specific league — you can reuse them across all your leagues.</li>
          <li>Each venue has a name, optional address, and field count (how many simultaneous games it can host).</li>
          <li>Assign venues to time slots during league creation (Step 3), or per-game from the Game Management page.</li>
          <li>The system detects conflicts when you try to assign more games to a venue than its field count allows at the same time.</li>
        </ul>
        <p>Venues show up on the public page, in calendar feeds, and in the PDF export.</p>
      </>
    ),
  },
  {
    title: 'Managing Referees',
    content: (
      <>
        <p>Referees are available on the Commissioner (Pro) plan. Manage them from the <strong>Referees</strong> page in the navigation bar.</p>
        <ul>
          <li>Like venues and captains, referees belong to your account and can be assigned across leagues.</li>
          <li>You can tag referees with sports they're qualified for — only matching referees show up when assigning.</li>
          <li>Assign referees to games from the Game Management page (individually or in bulk).</li>
          <li>Generate magic links so referees can view their schedule and submit scores through a dedicated portal.</li>
        </ul>
        <p>The sport-specific title is used throughout the app — "Umpire" for softball, "Official" for football, "Referee" for most other sports.</p>
      </>
    ),
  },
  {
    title: 'Sharing with Team Captains',
    content: (
      <>
        <p>There are two ways to share schedule info with captains:</p>
        <h4>Public Page</h4>
        <p>Share your league's public URL. Anyone with the link can view the schedule, standings, and subscribe to calendar feeds.</p>
        <h4>Captain Portal (Starter plan and above)</h4>
        <p>Generate personalized magic links for each captain:</p>
        <ol>
          <li>Make sure your teams have captains assigned with email addresses.</li>
          <li>On the Dashboard, click <strong>Captain Links</strong> on your league card.</li>
          <li>Copy the generated links and share them with each captain.</li>
        </ol>
        <p>Each captain's portal shows their team's schedule, league standings (with their team highlighted), and lets them update their contact info. If "Captain Score Submission" is enabled, they can also submit scores for your approval.</p>
      </>
    ),
  },
  {
    title: 'Email Notifications',
    content: (
      <>
        <p>Email features are available on the League (Starter) plan and above:</p>
        <ul>
          <li><strong>Notify Captains</strong> — Email each captain their team's schedule. Useful when you first publish.</li>
          <li><strong>Send Reminders</strong> — Send reminders for games happening in the next 48 hours.</li>
          <li><strong>Score update emails</strong> — Toggle "Email Scores" on a league card to auto-email captains when scores are entered.</li>
        </ul>
        <p>All emails include an unsubscribe link. Captains who unsubscribe for a specific league won't receive further emails for that league.</p>
      </>
    ),
  },
  {
    title: 'Playoffs',
    content: (
      <>
        <p>Single-elimination playoffs are available on the Commissioner (Pro) plan.</p>
        <ol>
          <li>Enable playoffs in Step 1 when creating your league (or edit the league later).</li>
          <li>Choose how many teams qualify (2, 4, 6, 8, etc.).</li>
          <li>Once regular season games are scored, go to <strong>Playoffs</strong> on the league card.</li>
          <li>Set dates and times for each playoff round, then generate the bracket.</li>
          <li>Enter scores for each matchup — winners advance automatically.</li>
        </ol>
        <p>Playoff brackets appear on the public page and in past season archives. If needed, you can manually override which team advances from any matchup.</p>
      </>
    ),
  },
  {
    title: 'Season Archiving',
    content: (
      <>
        <p>Season archiving is available on the Commissioner (Pro) plan. It lets you save a completed season and start fresh with the same teams.</p>
        <ol>
          <li>On the Dashboard, click <strong>Archive Season</strong> on a published league.</li>
          <li>Give the season a name (e.g., "Spring 2026").</li>
          <li>The current schedule, standings, and playoff results are saved as a snapshot.</li>
          <li>The league resets to Draft with all teams preserved and stats zeroed out.</li>
        </ol>
        <p>View past seasons from the <strong>Past Seasons</strong> button on the Dashboard, or from the "Past Seasons" dropdown on the public page.</p>
      </>
    ),
  },
  {
    title: 'Embedding on Your Website',
    content: (
      <>
        <p>You can embed your league schedule on any external website (Wix, Squarespace, WordPress, etc.):</p>
        <ol>
          <li>Publish your league and turn on <strong>Public Page</strong>.</li>
          <li>Click the <strong>Embed</strong> button on the league card.</li>
          <li>Copy the provided iframe code and paste it into your website's HTML.</li>
        </ol>
        <p>The embed widget includes a schedule view and standings table. It auto-resizes to fit the content, and updates automatically as you enter scores.</p>
      </>
    ),
  },
  {
    title: 'PDF Export',
    content: (
      <>
        <p>Download your schedule as a professional PDF at any time by clicking <strong>PDF</strong> on a league card.</p>
        <ul>
          <li>The PDF includes a title page, full league schedule, and individual team pages.</li>
          <li>Draft leagues show a "DRAFT" watermark and include the fairness report as an appendix.</li>
          <li>Venue and referee assignments are included when present.</li>
          <li>Free plan PDFs always show a DRAFT watermark. Starter and Commissioner plans get clean exports.</li>
        </ul>
      </>
    ),
  },
];

function AccordionSection({ title, content, isOpen, onToggle }) {
  return (
    <div className={`help-accordion ${isOpen ? 'help-accordion-open' : ''}`}>
      <button className="help-accordion-header" onClick={onToggle} aria-expanded={isOpen}>
        <span className="help-accordion-title">{title}</span>
        <span className="help-accordion-icon">{isOpen ? '\u2212' : '+'}</span>
      </button>
      {isOpen && (
        <div className="help-accordion-body">
          {content}
        </div>
      )}
    </div>
  );
}

export default function HelpPage({ onBack }) {
  const { theme, toggleTheme } = useTheme();
  const [openIndex, setOpenIndex] = useState(null);

  // If rendered as a public route (no onBack), override body background
  useEffect(() => {
    if (!onBack) {
      const root = document.documentElement;
      const body = document.body;
      const origRootBg = root.style.background;
      const origBodyBg = body.style.background;
      root.style.background = 'var(--color-bg)';
      body.style.background = 'var(--color-bg)';
      return () => {
        root.style.background = origRootBg;
        body.style.background = origBodyBg;
      };
    }
  }, [onBack]);

  const toggleSection = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Public route: render with its own header/footer
  if (!onBack) {
    return (
      <div className="help-page help-page-public">
        <nav className="help-nav">
          <Link to="/" className="help-nav-logo">
            <Logo size="1.4rem" /> LeagueMaker
          </Link>
          <div className="help-nav-actions">
            <button
              className="help-theme-toggle"
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? '\u2600' : '\uD83C\uDF19'}
            </button>
            <Link to="/" className="help-nav-link">Back to Home</Link>
          </div>
        </nav>
        <HelpContent
          openIndex={openIndex}
          onToggle={toggleSection}
        />
        <footer className="help-footer">
          <div className="help-footer-links">
            <Link to="/terms" className="help-footer-link">Terms</Link>
            <span className="help-footer-separator">|</span>
            <Link to="/privacy" className="help-footer-link">Privacy</Link>
          </div>
          <p>&copy; {new Date().getFullYear()} LeagueMaker. All rights reserved.</p>
        </footer>
      </div>
    );
  }

  // Authenticated route: rendered inside app-body with NavBar
  return (
    <div className="help-page">
      <div className="help-header-bar">
        <button className="help-back-btn" onClick={onBack}>&larr; Back</button>
        <h1 className="help-header-title">Help &amp; How-To</h1>
      </div>
      <HelpContent
        openIndex={openIndex}
        onToggle={toggleSection}
      />
    </div>
  );
}

function HelpContent({ openIndex, onToggle }) {
  return (
    <div className="help-content">
      <div className="help-hero">
        <h1 className="help-hero-title">How to Use LeagueMaker</h1>
        <p className="help-hero-subtitle">
          Everything you need to know to create, manage, and share your league.
        </p>
      </div>

      <div className="help-sections">
        {SECTIONS.map((section, index) => (
          <AccordionSection
            key={index}
            title={section.title}
            content={section.content}
            isOpen={openIndex === index}
            onToggle={() => onToggle(index)}
          />
        ))}
      </div>

      <div className="help-contact">
        <h2 className="help-contact-title">Still need help?</h2>
        <p className="help-contact-text">
          Check our <Link to="/faq" className="help-faq-link">Frequently Asked Questions</Link> for quick answers, or reach out and we'll get back to you as soon as we can.
        </p>
        <a href="mailto:support@leaguemaker.app" className="help-contact-link">
          support@leaguemaker.app
        </a>
      </div>
    </div>
  );
}
